import { useState, useRef } from "react";
import { toast } from "sonner";
import { UploadCloud, X, ArrowRight, CheckCircle2, Loader2, Phone, Mail, MessageSquare } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { api, formatApiErrorDetail } from "../lib/api";
import { SERVICE_OPTIONS } from "../data/content";
import { useContent } from "../context/ContentContext";

const CONTACT_METHODS = [
  { value: "Phone", icon: Phone },
  { value: "Email", icon: Mail },
  { value: "Text", icon: MessageSquare },
];

const initial = {
  name: "", phone: "", email: "", address: "", city: "",
  service: "", yard_details: "", preferred_contact: "Phone",
};

export default function Quote() {
  const { content } = useContent();
  const [form, setForm] = useState(initial);
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [hp, setHp] = useState(""); // honeypot — must stay empty for real users
  const fileRef = useRef(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFiles = (files) => {
    const incoming = Array.from(files)
      .filter((f) => f.type.startsWith("image/"))
      .map((file) => ({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
        file,
      }));
    setPhotos((p) => [...p, ...incoming].slice(0, 8));
  };

  const removePhoto = (id) => setPhotos((p) => p.filter((x) => x.id !== id));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.address || !form.city || !form.service) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("company_website", hp);
      photos.forEach((p) => fd.append("photos", p.file));
      await api.post("/quotes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success("Quote request sent! We'll be in touch soon.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6 py-24" data-testid="quote-success">
        <div className="max-w-lg text-center">
          <span className="grid place-items-center w-20 h-20 rounded-full bg-emerald/10 text-emerald mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10" />
          </span>
          <h1 className="font-display text-3xl sm:text-4xl font-600 text-charcoal mb-4">Request received!</h1>
          <p className="text-olive text-lg leading-relaxed mb-8">
            Thanks, {form.name.split(" ")[0]}. Adrian, David, or a member of our crew will reach out
            via {form.preferred_contact.toLowerCase()} soon to discuss your project and put together a fair quote.
          </p>
          <button
            onClick={() => { setForm(initial); setPhotos([]); setDone(false); }}
            className="inline-flex items-center gap-2 bg-emerald text-white px-7 py-3.5 rounded-sm font-600 hover:bg-emerald-dark transition-colors"
            data-testid="quote-another-btn"
          >
            Submit another request
          </button>
        </div>
      </div>
    );
  }

  const inputCls = "w-full border-b-2 border-border bg-transparent px-0 py-3 focus:outline-none focus:border-emerald transition-colors rounded-none placeholder:text-olive/50 text-charcoal";
  const labelCls = "block text-sm font-600 text-charcoal mb-1";

  return (
    <div data-testid="quote-page">
      <section className="bg-charcoal text-white py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><Eyebrow>Request a Quote</Eyebrow></Reveal>
          <Reveal delay={1}>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-700 mt-5 max-w-3xl leading-[1.08] tracking-tight text-balance">
              Let's build something that lasts.
            </h1>
          </Reveal>
          <Reveal delay={2}>
            <p className="text-white/75 text-lg mt-6 max-w-2xl leading-relaxed">
              Tell us about your property and what you're looking for. Add a few photos and we'll
              get you a fair, no-pressure quote. Prefer to talk? Call <span className="text-gold font-600">{content.contact.phone}</span>.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <form onSubmit={handleSubmit} className="bg-white border border-border rounded-sm p-8 md:p-12 space-y-8" data-testid="quote-form">
            {/* Honeypot: hidden from real users; bots that fill it are silently rejected */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
              <label htmlFor="company_website">Company Website</label>
              <input
                id="company_website"
                name="company_website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={hp}
                onChange={(e) => setHp(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              <div>
                <label className={labelCls}>Full Name *</label>
                <input data-testid="quote-name" className={inputCls} value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="John Smith" />
              </div>
              <div>
                <label className={labelCls}>Phone *</label>
                <input data-testid="quote-phone" className={inputCls} value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(314) 386-6993" />
              </div>
              <div>
                <label className={labelCls}>Email *</label>
                <input data-testid="quote-email" type="email" className={inputCls} value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" />
              </div>
              <div>
                <label className={labelCls}>Street Address *</label>
                <input data-testid="quote-address" className={inputCls} value={form.address} onChange={(e) => update("address", e.target.value)} placeholder="123 Magnolia St" />
              </div>
              <div>
                <label className={labelCls}>City *</label>
                <input data-testid="quote-city" list="cities" className={inputCls} value={form.city} onChange={(e) => update("city", e.target.value)} placeholder="Rincon" />
                <datalist id="cities">
                  {content.contact.serviceAreas.map((a) => <option key={a} value={a} />)}
                </datalist>
              </div>
              <div>
                <label className={labelCls}>Service Needed *</label>
                <select data-testid="quote-service" className={`${inputCls} cursor-pointer`} value={form.service} onChange={(e) => update("service", e.target.value)}>
                  <option value="" disabled>Select a service</option>
                  {SERVICE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className={labelCls}>Yard Details</label>
              <textarea data-testid="quote-details" rows={4} className={`${inputCls} resize-none`} value={form.yard_details} onChange={(e) => update("yard_details", e.target.value)} placeholder="Approx. yard size, current condition, specific problem areas, what you'd like done…" />
            </div>

            {/* Photos */}
            <div>
              <label className={labelCls}>Photo Uploads <span className="font-400 text-olive/70">(optional, up to 8)</span></label>
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
                className="mt-2 border-2 border-dashed border-border rounded-sm p-8 text-center cursor-pointer hover:border-emerald hover:bg-emerald/5 transition-colors"
                data-testid="quote-photo-dropzone"
              >
                <UploadCloud className="w-8 h-8 text-emerald mx-auto mb-2" />
                <p className="text-charcoal font-500">Drag photos here, or click to browse</p>
                <p className="text-olive/60 text-sm mt-1">JPG, PNG, or WEBP</p>
                <input ref={fileRef} data-testid="quote-photo-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
              </div>
              {photos.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4" data-testid="quote-photo-previews">
                  {photos.map((p, i) => (
                    <div key={p.id} className="relative group aspect-square">
                      <img src={URL.createObjectURL(p.file)} alt={`upload-${i}`} className="w-full h-full object-cover rounded-sm" />
                      <button type="button" onClick={() => removePhoto(p.id)} className="absolute top-1 right-1 bg-charcoal/80 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-photo-${i}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Preferred contact */}
            <div>
              <label className={labelCls}>Preferred Contact Method</label>
              <div className="flex flex-wrap gap-3 mt-2">
                {CONTACT_METHODS.map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => update("preferred_contact", m.value)}
                    data-testid={`contact-method-${m.value.toLowerCase()}`}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-sm border font-500 transition-colors ${
                      form.preferred_contact === m.value
                        ? "bg-emerald text-white border-emerald"
                        : "bg-white text-charcoal border-border hover:border-emerald"
                    }`}
                  >
                    <m.icon className="w-4 h-4" /> {m.value}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="quote-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-charcoal px-8 py-4 rounded-sm font-700 tracking-wide hover:bg-gold-dark transition-colors disabled:opacity-60"
            >
              {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Sending…</> : <>Send My Quote Request <ArrowRight className="w-5 h-5" /></>}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
